import React from "react";
import * as PropTypes from "prop-types";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";

/**
 * Modern rich text editor built on TipTap (ProseMirror).
 *
 * Replaces the legacy @progress/kendo-react-editor. Emits HTML through
 * onChange so callers stay drop-in compatible with the previous editor.
 *
 * Props:
 *   value         current HTML string
 *   onChange      (html) => void, fired on every doc update
 *   minHeight     content area minimum height in pixels (default 320)
 *   readOnly      disables editing when true
 *   ariaLabel     accessibility label for the editable region
 *   testId        passes a data-testid through to the editable surface
 */
const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    aria-pressed={active ? "true" : "false"}
    style={{
      padding: "4px 10px",
      border: "1px solid #ccc",
      borderRadius: 4,
      background: active ? "#dce8f7" : "#fff",
      color: disabled ? "#999" : "#222",
      cursor: disabled ? "not-allowed" : "pointer",
      fontSize: 13,
      lineHeight: 1.4,
      fontWeight: active ? 600 : 400,
    }}
  >
    {children}
  </button>
);

const ToolbarDivider = () => (
  <span
    aria-hidden="true"
    style={{
      width: 1,
      alignSelf: "stretch",
      background: "#ddd",
      margin: "0 4px",
    }}
  />
);

const RichTextEditor = ({
  value,
  onChange,
  minHeight = 320,
  readOnly = false,
  ariaLabel = "Rich text editor",
  testId = "rich-text-editor",
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      if (onChange) {
        const html = ed.getHTML();
        onChange(html === "<p></p>" ? "" : html);
      }
    },
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        "data-testid": testId,
        role: "textbox",
        "aria-multiline": "true",
        style: `min-height: ${minHeight}px; padding: 12px; outline: none;`,
      },
    },
  });

  // Keep the editor in sync if the caller resets the value externally
  // (e.g. when selecting a different template from the list).
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    const normalizedCurrent = current === "<p></p>" ? "" : current;
    if (normalizedCurrent !== incoming) {
      editor.commands.setContent(incoming || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  React.useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  const promptForLink = React.useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href || "";
    // eslint-disable-next-line no-alert
    const url = window.prompt("Enter URL", previousUrl);
    if (url === null) return; // cancelled
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  }, [editor]);

  if (!editor) {
    return (
      <div
        data-testid={`${testId}-loading`}
        style={{
          border: "1px solid #ccc",
          borderRadius: 4,
          minHeight: minHeight + 50,
          padding: 12,
          color: "#888",
        }}
      >
        Loading editor...
      </div>
    );
  }

  const can = editor.can();

  return (
    <div
      style={{
        border: "1px solid #ccc",
        borderRadius: 4,
        background: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        role="toolbar"
        aria-label="Formatting"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          padding: 8,
          borderBottom: "1px solid #eee",
          background: "#fafafa",
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      >
        <ToolbarButton
          title="Bold"
          active={editor.isActive("bold")}
          disabled={readOnly}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          active={editor.isActive("italic")}
          disabled={readOnly}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          title="Underline"
          active={editor.isActive("underline")}
          disabled={readOnly}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <span style={{ textDecoration: "underline" }}>U</span>
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          title="Align left"
          active={editor.isActive({ textAlign: "left" })}
          disabled={readOnly}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
        >
          ⯇
        </ToolbarButton>
        <ToolbarButton
          title="Align center"
          active={editor.isActive({ textAlign: "center" })}
          disabled={readOnly}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
        >
          ≡
        </ToolbarButton>
        <ToolbarButton
          title="Align right"
          active={editor.isActive({ textAlign: "right" })}
          disabled={readOnly}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
        >
          ⯈
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          title="Bullet list"
          active={editor.isActive("bulletList")}
          disabled={readOnly}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • List
        </ToolbarButton>
        <ToolbarButton
          title="Numbered list"
          active={editor.isActive("orderedList")}
          disabled={readOnly}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1. List
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          title="Add or edit link"
          active={editor.isActive("link")}
          disabled={readOnly}
          onClick={promptForLink}
        >
          Link
        </ToolbarButton>
        <ToolbarButton
          title="Remove link"
          disabled={readOnly || !editor.isActive("link")}
          onClick={() =>
            editor.chain().focus().extendMarkRange("link").unsetLink().run()
          }
        >
          Unlink
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          title="Undo"
          disabled={readOnly || !can.undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          ↶
        </ToolbarButton>
        <ToolbarButton
          title="Redo"
          disabled={readOnly || !can.redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          ↷
        </ToolbarButton>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
};

RichTextEditor.displayName = "RichTextEditor";
RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  minHeight: PropTypes.number,
  readOnly: PropTypes.bool,
  ariaLabel: PropTypes.string,
  testId: PropTypes.string,
};

export default RichTextEditor;
